import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useState } from "react";
import ToastContext from "../../contexts/ToastContext";
import gatewayApi from "../../api/gatewayApi";
import HowToPanel from "../../components/HowToPanel/HowToPanel";
import "./api-key-page.less";
const API_KEY_HOW_TO_STEPS = [
    "Generate your key using the button above — it is only shown once, so copy it immediately.",
    "Store it securely (e.g. in your password manager).",
    "Pick your tool below and add the config snippet — replace sk-hermes-... with your actual key.",
    "Each user can only have one active key. Generating a new one revokes the previous key."
];
const API_BASE_URL = process.env.GATEWAY_URL || window.location.origin;
const API_KEY_CODE_EXAMPLES = [
    {
        label: "OpenCode",
        code: `// ~/.config/opencode/opencode.json
{
  "$schema": "https://opencode.ai/config.json",
  "provider": {
    "hermes": {
      "npm": "@ai-sdk/openai-compatible",
      "name": "Hermes",
      "options": {
        "baseURL": "${API_BASE_URL}/v1",
        "apiKey": "sk-hermes-..."
      },
    }
  },
  "enabled_providers": ["hermes"]
}`
    },
    {
        label: "VS Code (Continue)",
        code: `# ~/.continue/config.yaml
models:
  - name: Hermes Gateway
    provider: openai
    apiBase: ${API_BASE_URL}/v1
    apiKey: sk-hermes-...
    model: <model-name>`
    }
];
export default function ApiKeyPage() {
    const { showToast } = useContext(ToastContext);
    const [status, setStatus] = useState(null);
    const [newKey, setNewKey] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRevoking, setIsRevoking] = useState(false);
    const [showConfirmRevoke, setShowConfirmRevoke] = useState(false);
    const [copied, setCopied] = useState(false);
    const loadStatus = useCallback(async () => {
        try {
            const s = await gatewayApi.getApiKeyStatus();
            setStatus(s);
        }
        catch {
            showToast("Failed to load API key status", "error");
        }
    }, [showToast]);
    useEffect(() => {
        void loadStatus();
    }, [loadStatus]);
    async function handleGenerate() {
        setIsGenerating(true);
        try {
            const issuance = await gatewayApi.generateApiKey();
            setNewKey(issuance);
            await loadStatus();
            showToast("API key generated successfully", "success");
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : "Failed to generate API key", "error");
        }
        finally {
            setIsGenerating(false);
        }
    }
    async function handleRevoke() {
        setIsRevoking(true);
        try {
            await gatewayApi.revokeApiKey();
            setStatus({ exists: false, issuedAt: null, keyPrefix: null });
            setNewKey(null);
            setShowConfirmRevoke(false);
            showToast("API key revoked", "success");
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : "Failed to revoke API key", "error");
        }
        finally {
            setIsRevoking(false);
        }
    }
    function handleCopy() {
        if (!newKey)
            return;
        void navigator.clipboard.writeText(newKey.token).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }
    return (_jsxs("div", { className: "HGU__page-content", children: [_jsxs("div", { className: "HGU__page-header", children: [_jsx("h1", { className: "HGU__page-title", children: "API Key" }), _jsx("p", { className: "HGU__page-subtitle", children: "Use an API key to authenticate CLI tools and scripts with the Hermes Gateway. Each user can have one active key at a time." })] }), newKey && (_jsxs("div", { className: "HGU__alert HGU__alert--success", children: [_jsxs("div", { className: "HGU__alert-header", children: [_jsx("strong", { children: "Your new API key \u2014 save it now" }), _jsx("span", { className: "HGU__alert-note", children: "It will not be shown again." })] }), _jsxs("div", { className: "HGU__key-display", children: [_jsx("code", { className: "HGU__key-value", children: newKey.token }), _jsx("button", { className: "HGU__btn HGU__btn--ghost", onClick: handleCopy, children: copied ? "Copied!" : "Copy" })] }), _jsx("p", { className: "HGU__alert-message", children: newKey.message })] })), _jsxs("div", { className: "HGU__card", children: [_jsx("div", { className: "HGU__card-header", children: _jsx("h2", { className: "HGU__card-title", children: "Current Key" }) }), status === null ? (_jsx("p", { className: "HGU__card-loading", children: "Loading..." })) : status.exists ? (_jsxs("div", { children: [_jsxs("div", { className: "HGU__info-list", children: [_jsxs("div", { className: "HGU__info-row", children: [_jsx("span", { className: "HGU__info-label", children: "Status" }), _jsx("span", { className: "HGU__status HGU__status--ok", children: "Active" })] }), _jsxs("div", { className: "HGU__info-row", children: [_jsx("span", { className: "HGU__info-label", children: "Prefix" }), _jsx("code", { className: "HGU__code", children: status.keyPrefix })] }), _jsxs("div", { className: "HGU__info-row", children: [_jsx("span", { className: "HGU__info-label", children: "Issued" }), _jsx("span", { className: "HGU__info-value", children: status.issuedAt ? new Date(status.issuedAt).toLocaleString() : "—" })] })] }), _jsxs("div", { className: "HGU__action-row", children: [_jsx("button", { className: "HGU__btn HGU__btn--primary", onClick: handleGenerate, disabled: isGenerating, children: isGenerating ? "Generating..." : "Regenerate key" }), !showConfirmRevoke ? (_jsx("button", { className: "HGU__btn HGU__btn--danger-outline", onClick: () => setShowConfirmRevoke(true), children: "Revoke key" })) : (_jsxs("div", { className: "HGU__confirm-row", children: [_jsx("span", { className: "HGU__confirm-text", children: "Are you sure?" }), _jsx("button", { className: "HGU__btn HGU__btn--danger", onClick: handleRevoke, disabled: isRevoking, children: isRevoking ? "Revoking..." : "Yes, revoke" }), _jsx("button", { className: "HGU__btn HGU__btn--ghost", onClick: () => setShowConfirmRevoke(false), children: "Cancel" })] }))] })] })) : (_jsxs("div", { className: "HGU__card-empty", children: [_jsx("p", { children: "You don't have an active API key." }), _jsx("button", { className: "HGU__btn HGU__btn--primary", onClick: handleGenerate, disabled: isGenerating, children: isGenerating ? "Generating..." : "Generate API key" })] })), _jsx(HowToPanel, { steps: API_KEY_HOW_TO_STEPS, codeBlocks: API_KEY_CODE_EXAMPLES })] })] }));
}
